import React, { useState, useEffect, useCallback, useRef } from "react";
import { datasetsAPI } from "../../utils/datasetsAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { useAuth } from "../../context/AuthContext";
import { LoadingSpinner, SkeletonTable } from "../../components/Loaders";
import { ErrorState } from "../../components/EmptyStates";

function DataTables() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    page_size: 25,
    has_next: false,
    has_previous: false,
  });
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [search, setSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Debounce timer
  const searchTimer = useRef(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadDatasets();
    }
  }, [selectedOrgId]);

  // Reload rows when dataset, page, pageSize, sort, date range, or column filters change
  useEffect(() => {
    if (selectedDatasetId && selectedOrgId) {
      loadTableRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDatasetId,
    selectedOrgId,
    currentPage,
    pageSize,
    sortBy,
    sortDir,
    dateFrom,
    dateTo,
    columnFilters,
  ]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (selectedDatasetId && selectedOrgId) {
        setCurrentPage(1);
        loadTableRows();
      }
    }, 400);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationsAPI.getAccessible();
      const data = response?.data || response;
      const orgsList = Array.isArray(data) ? data : data?.results || [];
      setOrganizations(orgsList);

      if (orgsList.length > 0) {
        setSelectedOrgId(orgsList[0].id);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDatasets = async () => {
    if (!selectedOrgId) return;

    try {
      setLoading(true);
      const response = await datasetsAPI.list(selectedOrgId, {
        page_size: 100,
      });
      const data = response?.data || response;
      const datasetsList = Array.isArray(data) ? data : data?.results || [];
      setDatasets(datasetsList);

      if (datasetsList.length > 0) {
        setSelectedDatasetId(datasetsList[0].id);
      } else {
        setSelectedDatasetId(null);
        setRows([]);
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: 0,
          page_size: 25,
          has_next: false,
          has_previous: false,
        });
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTableRows = async () => {
    if (!selectedDatasetId || !selectedOrgId) return;

    try {
      setRowsLoading(true);
      const params = {
        page: currentPage,
        page_size: pageSize,
      };
      if (search) params.search = search;
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
      }
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      // Collect per-column filters
      const activeFilters = {};
      Object.entries(columnFilters).forEach(([key, val]) => {
        if (val !== "" && val !== null && val !== undefined) {
          activeFilters[key] = val;
        }
      });
      if (Object.keys(activeFilters).length > 0) {
        params.filters = activeFilters;
      }

      const response = await datasetsAPI.listRows(
        selectedOrgId,
        selectedDatasetId,
        params,
      );
      const resData = response?.data || response;

      // Handle paginated response
      if (resData?.data) {
        setRows(resData.data);
        if (resData.pagination) {
          setPagination(resData.pagination);
        }
      } else {
        const rowsList = Array.isArray(resData)
          ? resData
          : resData?.results || [];
        setRows(rowsList);
      }
    } catch (err) {
      console.error("Failed to load rows:", err);
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  };

  const handleOrgChange = (orgId) => {
    setSelectedOrgId(Number(orgId));
    setSelectedDatasetId(null);
    setDatasets([]);
    setRows([]);
    resetFilters();
  };

  const handleDatasetChange = (dsId) => {
    setSelectedDatasetId(Number(dsId));
    resetFilters();
  };

  const resetFilters = () => {
    setSearch("");
    setColumnFilters({});
    setSortBy("");
    setSortDir("desc");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  const handleSort = (colKey) => {
    if (sortBy === colKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(colKey);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setCurrentPage(newPage);
    }
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (dateFrom || dateTo ? 1 : 0) +
    Object.values(columnFilters).filter(
      (v) => v !== "" && v !== null && v !== undefined,
    ).length;

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <ErrorState error={error} onRetry={loadOrganizations} />
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="page-content">
        <div className="card">
          <h2 className="card-title">Client Data Tables</h2>
          <p
            style={{
              padding: "16px",
              textAlign: "center",
              color: "var(--text-grey)",
            }}
          >
            No client organizations assigned to you yet.
          </p>
        </div>
      </div>
    );
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);
  const selectedDataset = datasets.find((d) => d.id === selectedDatasetId);
  const columns = selectedDataset?.columns || [];

  const renderSortIcon = (colKey) => {
    if (sortBy !== colKey)
      return (
        <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 12 }}>⇅</span>
      );
    return (
      <span style={{ marginLeft: 4, fontSize: 12 }}>
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // Build page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const total = pagination.total_pages;
    const current = pagination.current_page;
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push("...");
      for (
        let i = Math.max(2, current - 1);
        i <= Math.min(total - 1, current + 1);
        i++
      ) {
        pages.push(i);
      }
      if (current < total - 2) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  return (
    <div className="page-content">
      <div className="card">
        {/* Header: title + org/dataset selectors */}
        <div
          className="card-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <h2 className="card-title" style={{ margin: 0 }}>
            Client Data Tables
          </h2>
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <label style={{ fontSize: "12px", color: "var(--text-grey)" }}>
                Client Organization
              </label>
              <select
                className="btn btn-secondary"
                value={selectedOrgId || ""}
                onChange={(e) => handleOrgChange(e.target.value)}
                style={{ minWidth: "200px" }}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            {datasets.length > 0 && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <label style={{ fontSize: "12px", color: "var(--text-grey)" }}>
                  Dataset
                </label>
                <select
                  className="btn btn-secondary"
                  value={selectedDatasetId || ""}
                  onChange={(e) => handleDatasetChange(e.target.value)}
                  style={{ minWidth: "200px" }}
                >
                  {datasets.map((ds) => (
                    <option key={ds.id} value={ds.id}>
                      {ds.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Search bar + filter toggle */}
        {selectedDatasetId && columns.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "12px",
            }}
          >
            <div
              style={{ position: "relative", flex: "1 1 300px", maxWidth: 400 }}
            >
              <input
                type="text"
                placeholder="Search across all columns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 34px",
                  border: "1px solid var(--border-color, #d1d5db)",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  background: "var(--bg-white, #fff)",
                  color: "var(--text-dark, #111)",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 16,
                  opacity: 0.45,
                }}
              >
                🔍
              </span>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ fontSize: 15 }}>⚙</span>
              Filters
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "var(--primary-color, #3b82f6)",
                    color: "#fff",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                className="btn"
                onClick={resetFilters}
                style={{
                  padding: "8px 14px",
                  fontSize: 13,
                  color: "var(--danger-color, #ef4444)",
                  background: "transparent",
                  border: "1px solid var(--danger-color, #ef4444)",
                  borderRadius: 6,
                }}
              >
                Clear All
              </button>
            )}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <label style={{ fontSize: 12, color: "var(--text-grey)" }}>
                Rows/page
              </label>
              <select
                className="btn btn-secondary"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: "6px 10px", fontSize: 13, minWidth: 70 }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Expandable filter panel */}
        {showFilters && selectedDatasetId && columns.length > 0 && (
          <div
            style={{
              padding: "14px 16px",
              background: "var(--bg-light, #f9fafb)",
              borderRadius: "8px",
              marginBottom: "14px",
              border: "1px solid var(--border-color, #e5e7eb)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                Column Filters
              </h4>
            </div>
            {/* Date range filter */}
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label
                  style={{
                    fontSize: 11,
                    color: "var(--text-grey)",
                    fontWeight: 500,
                  }}
                >
                  Created From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid var(--border-color, #d1d5db)",
                    borderRadius: 6,
                    fontSize: 13,
                    background: "var(--bg-white, #fff)",
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <label
                  style={{
                    fontSize: 11,
                    color: "var(--text-grey)",
                    fontWeight: 500,
                  }}
                >
                  Created To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid var(--border-color, #d1d5db)",
                    borderRadius: 6,
                    fontSize: 13,
                    background: "var(--bg-white, #fff)",
                  }}
                />
              </div>
            </div>
            {/* Per-column filters */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 10,
              }}
            >
              {columns.map((col) => {
                if (col.column_type === "number") {
                  return (
                    <div
                      key={col.key}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          color: "var(--text-grey)",
                          fontWeight: 500,
                        }}
                      >
                        {col.label || col.key}
                      </label>
                      <div style={{ display: "flex", gap: 6 }}>
                        <input
                          type="number"
                          placeholder="Min"
                          value={columnFilters[`${col.key}__gte`] || ""}
                          onChange={(e) =>
                            handleColumnFilterChange(
                              `${col.key}__gte`,
                              e.target.value,
                            )
                          }
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            border: "1px solid var(--border-color, #d1d5db)",
                            borderRadius: 6,
                            fontSize: 13,
                            background: "var(--bg-white, #fff)",
                            minWidth: 0,
                          }}
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={columnFilters[`${col.key}__lte`] || ""}
                          onChange={(e) =>
                            handleColumnFilterChange(
                              `${col.key}__lte`,
                              e.target.value,
                            )
                          }
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            border: "1px solid var(--border-color, #d1d5db)",
                            borderRadius: 6,
                            fontSize: 13,
                            background: "var(--bg-white, #fff)",
                            minWidth: 0,
                          }}
                        />
                      </div>
                    </div>
                  );
                }
                if (col.column_type === "date") {
                  return (
                    <div
                      key={col.key}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          color: "var(--text-grey)",
                          fontWeight: 500,
                        }}
                      >
                        {col.label || col.key}
                      </label>
                      <input
                        type="date"
                        value={columnFilters[col.key] || ""}
                        onChange={(e) =>
                          handleColumnFilterChange(col.key, e.target.value)
                        }
                        style={{
                          padding: "6px 8px",
                          border: "1px solid var(--border-color, #d1d5db)",
                          borderRadius: 6,
                          fontSize: 13,
                          background: "var(--bg-white, #fff)",
                        }}
                      />
                    </div>
                  );
                }
                // text
                return (
                  <div
                    key={col.key}
                    style={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <label
                      style={{
                        fontSize: 11,
                        color: "var(--text-grey)",
                        fontWeight: 500,
                      }}
                    >
                      {col.label || col.key}
                    </label>
                    <input
                      type="text"
                      placeholder={`Filter ${col.label || col.key}...`}
                      value={columnFilters[col.key] || ""}
                      onChange={(e) =>
                        handleColumnFilterChange(col.key, e.target.value)
                      }
                      style={{
                        padding: "6px 8px",
                        border: "1px solid var(--border-color, #d1d5db)",
                        borderRadius: 6,
                        fontSize: 13,
                        background: "var(--bg-white, #fff)",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Read-only banner */}
        <div
          style={{
            padding: "10px 16px",
            background: "#FEF3C7",
            borderRadius: "8px",
            marginBottom: "14px",
          }}
        >
          <p style={{ fontSize: "13px", color: "var(--text-dark)", margin: 0 }}>
            <strong>Read-Only Mode:</strong> You can view but not edit client
            data. Use filters to query specific data.
          </p>
        </div>

        {/* Table */}
        {datasets.length === 0 ? (
          <p
            style={{
              padding: "16px",
              textAlign: "center",
              color: "var(--text-grey)",
            }}
          >
            No datasets found for {selectedOrg?.name}
          </p>
        ) : rowsLoading ? (
          <SkeletonTable rows={5} cols={columns.length || 3} />
        ) : rows.length > 0 && columns.length > 0 ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => handleSort("created_at")}
                    >
                      #
                    </th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label || col.key}
                        {renderSortIcon(col.key)}
                      </th>
                    ))}
                    <th
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => handleSort("created_at")}
                    >
                      Created {renderSortIcon("created_at")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id}>
                      <td style={{ color: "var(--text-grey)", fontSize: 12 }}>
                        {(currentPage - 1) * pageSize + idx + 1}
                      </td>
                      {columns.map((col) => (
                        <td key={col.key}>{row.data?.[col.key] ?? "-"}</td>
                      ))}
                      <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                        {new Date(row.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "14px",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <p style={{ fontSize: 13, color: "var(--text-grey)", margin: 0 }}>
                Showing{" "}
                {Math.min(
                  (currentPage - 1) * pageSize + 1,
                  pagination.total_items,
                )}
                –{Math.min(currentPage * pageSize, pagination.total_items)} of{" "}
                <strong>{pagination.total_items}</strong> rows
              </p>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button
                  className="btn btn-secondary"
                  disabled={!pagination.has_previous}
                  onClick={() => handlePageChange(1)}
                  style={{ padding: "5px 10px", fontSize: 13 }}
                  title="First page"
                >
                  «
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={!pagination.has_previous}
                  onClick={() => handlePageChange(currentPage - 1)}
                  style={{ padding: "5px 10px", fontSize: 13 }}
                >
                  ‹ Prev
                </button>
                {getPageNumbers().map((p, i) =>
                  p === "..." ? (
                    <span
                      key={`dots-${i}`}
                      style={{
                        padding: "5px 6px",
                        fontSize: 13,
                        color: "var(--text-grey)",
                      }}
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`btn ${p === currentPage ? "btn-primary" : "btn-secondary"}`}
                      onClick={() => handlePageChange(p)}
                      style={{
                        padding: "5px 10px",
                        fontSize: 13,
                        fontWeight: p === currentPage ? 700 : 400,
                      }}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  className="btn btn-secondary"
                  disabled={!pagination.has_next}
                  onClick={() => handlePageChange(currentPage + 1)}
                  style={{ padding: "5px 10px", fontSize: 13 }}
                >
                  Next ›
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={!pagination.has_next}
                  onClick={() => handlePageChange(pagination.total_pages)}
                  style={{ padding: "5px 10px", fontSize: 13 }}
                  title="Last page"
                >
                  »
                </button>
              </div>
            </div>
          </>
        ) : (
          <p
            style={{
              padding: "16px",
              textAlign: "center",
              color: "var(--text-grey)",
            }}
          >
            {activeFilterCount > 0
              ? "No rows match your current filters."
              : "No data rows in this dataset yet."}
          </p>
        )}

        {/* Dataset info footer */}
        <div
          style={{
            marginTop: "20px",
            padding: "14px 16px",
            backgroundColor: "var(--bg-light, #f9fafb)",
            borderRadius: "8px",
          }}
        >
          <h3 style={{ marginBottom: "10px", fontSize: 14 }}>
            Dataset Information
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "14px",
            }}
          >
            <div>
              <p
                style={{
                  fontSize: "11px",
                  color: "var(--text-grey)",
                  marginBottom: "3px",
                }}
              >
                Client
              </p>
              <p style={{ fontWeight: 600, fontSize: 14 }}>
                {selectedOrg?.name || "-"}
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: "11px",
                  color: "var(--text-grey)",
                  marginBottom: "3px",
                }}
              >
                Dataset
              </p>
              <p style={{ fontWeight: 600, fontSize: 14 }}>
                {selectedDataset?.name || "-"}
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: "11px",
                  color: "var(--text-grey)",
                  marginBottom: "3px",
                }}
              >
                Total Rows
              </p>
              <p style={{ fontWeight: 600, fontSize: 14 }}>
                {pagination.total_items}
              </p>
            </div>
            <div>
              <p
                style={{
                  fontSize: "11px",
                  color: "var(--text-grey)",
                  marginBottom: "3px",
                }}
              >
                Columns
              </p>
              <p style={{ fontWeight: 600, fontSize: 14 }}>{columns.length}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTables;
